import { getFragmentData } from '@packages/gql/generated/shopping';
import type {
  AddToBasketMutation,
  ChangeProductMutation,
  RawProductQuery,
} from '@packages/gql/generated/shopping/graphql';
import { TrackGlycerinAddToCartLineItemFragmentDoc } from '@packages/gql/generated/shopping/TrackGlycerinAddToCartLineItemFragmentDoc';
import type {
  GTMEventGlycerinAddToCart,
  GTMEventGlycerinUpdateCart,
} from '@packages/tracking/src/types/events';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import { useAtomValue } from 'jotai';
import { useSearchParams } from 'next/navigation';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from 'urql';
import { LineItemFragmentFragmentDoc } from '@packages/gql/generated/shopping/LineItemFragmentFragmentDoc';
import type { ProductPriceFragmentFragmentDoc } from '@packages/gql/generated/shopping/ProductPriceFragmentFragmentDoc';
import { unmask } from '@packages/gql/src/betterMasking';
import { BreadcrumbDocument } from '@packages/gql/generated/shopping/BreadcrumbDocument';
import { selectedQuantity, selectedServices } from '../../../atoms';

import { useHashParameters } from '../../../hooks/useHashParameters/useHashParameters';
import { getGlycerinArticleId } from '../../helper/getGlycerinArticleId/getGlycerinArticleId';
import { getGlycerinAvailability } from '../../helper/getGlycerinAvailability/getGlycerinAvailability';
import { getGlycerinOrigin } from '../../helper/getGlycerinOrigin/getGlycerinOrigin';

import { buildGlycerinCartProduct } from '../../../helpers/buildGlycerinObject/buildGlycerinObject';
import { getGlycerinServices } from '../../helper/getGlycerinServices/getGlycerinServices';
import type { GetGlycerinServicesProps } from '../../helper/getGlycerinServices/getGlycerinServices';
import { getAddToBasketColorString } from './getAddToBasketColorString/getAddToBasketColorString';
import { getAddToBasketVariantString } from './getAddToBasketVariantString/getAddToBasketVariantString';
import type { AddToBasketTrackingParameter } from './types';
import { getAplusDataForTracking } from '../../../APlus/helper/getAplusDataForTracking';

/* GraphQL */ `
  fragment TrackGlycerinAddToCartLineItem on BasketLineItem {
    extra {
      promotion
    }
    product {
      sku
      orderNumber
      name
      size
      variation
    }
  }
`;

type RawProduct = NonNullable<RawProductQuery['product']>;

export type AddToBasketTrackingData = Pick<
  RawProduct,
  | 'akl'
  | 'brand'
  | 'defaultCategory'
  | 'dimensions'
  | 'longDescription'
  | 'marketcode'
  | 'media'
  | 'name'
  | 'orderNumber'
  | 'promotion'
  | 'sku'
  | 'price'
> & {
  availability: Pick<NonNullable<RawProduct['availability']>, 'isOrderable' | 'state' | 'message'>;
};

export type UseAddToBasketTrackingProps = {
  trackingProductData: AddToBasketTrackingData;
  addToBasketTrackingAplus?: GetGlycerinServicesProps['aplus'];
};

export const useAddToBasketTracking = ({
  trackingProductData,
  addToBasketTrackingAplus,
}: UseAddToBasketTrackingProps) => {
  const {
    akl,
    availability,
    brand,
    defaultCategory,
    dimensions,
    longDescription,
    marketcode,
    media,
    name,
    orderNumber,
    price: priceData,
    promotion,
    sku,
  } = trackingProductData;

  const price = unmask<typeof ProductPriceFragmentFragmentDoc>(priceData);

  const searchParams = useSearchParams();
  const ref = searchParams?.get('ref') || null;
  const navq = searchParams?.get('nav-q') || null;

  const { category, searchNo } = useHashParameters();
  const { locale } = useIntl();
  const dispatchGTMEvent = useTracking();

  const quantity = useAtomValue(selectedQuantity);
  const aPlusData = useAtomValue(selectedServices);

  const categoryId = category || defaultCategory || undefined;

  const [{ data: breadCrumbData }] = useQuery({
    query: BreadcrumbDocument,
    variables: { categoryId: categoryId ?? '', locale },
    context: useMemo(() => ({ suspense: false }), []),
    pause: !categoryId,
  });

  const breadcrumbs: string[] = [];
  if (breadCrumbData?.breadcrumb?.breadCrumb) {
    breadCrumbData.breadcrumb.breadCrumb.forEach((item) => {
      breadcrumbs.push(item.name);
    });
  }
  const color = getAddToBasketColorString(dimensions);
  const variant = getAddToBasketVariantString(dimensions);

  // tracking parameters for atb request
  // implementation according to https://empiriecom.atlassian.net/browse/INFORM-2117
  const getTrackingParameter = (): AddToBasketTrackingParameter | undefined => {
    if (navq || searchNo) {
      return {
        source: 'Suche',
      };
    }
    if (categoryId && breadcrumbs) {
      return {
        source: categoryId,
        breadcrumbs,
      };
    }
    return undefined;
  };

  const trackLayerClosed = () => {
    dispatchGTMEvent({
      event: 'ga_event',
      eventAction: 'click',
      eventCategory: 'ATB_continueShopping',
      eventLabel: 'detailview_atb_modal',
    });
  };

  const trackToBasketClick = () => {
    dispatchGTMEvent({
      event: 'ga_event',
      eventAction: 'click',
      eventCategory: 'ATB_toBasket',
      eventLabel: 'detailview_atb_modal',
    });
  };

  const trackToCheckAndOrder = () => {
    dispatchGTMEvent({
      event: 'ga_event',
      eventAction: 'view',
      eventCategory: 'ATB_toCheckAndOrder',
      eventLabel: 'detailview_atb_modal',
    });
  };

  const trackUpdateCart = (
    lineItemData: ChangeProductMutation['changeProduct']['lineItems'],
    updatedLineItemId?: string,
  ) => {
    const fragmentLineItem = getFragmentData(LineItemFragmentFragmentDoc, lineItemData);
    const updatedLineItem = fragmentLineItem.find((item) => item.id === updatedLineItemId);

    if (updatedLineItem) {
      // found the updated line item update glycerin
      const glycerinProduct = buildGlycerinCartProduct(updatedLineItem);
      dispatchGTMEvent<GTMEventGlycerinUpdateCart>({
        event: 'UpdateCart',
        UpdateCartData: {
          updatedProducts: [glycerinProduct],
        },
      });
      return;
    }
    // last item in list if we can't find the updated one (shouldn't happen)
    const glycerinProduct = buildGlycerinCartProduct(fragmentLineItem[fragmentLineItem.length - 1]);
    dispatchGTMEvent<GTMEventGlycerinUpdateCart>({
      event: 'UpdateCart',
      UpdateCartData: {
        updatedProducts: [glycerinProduct],
      },
    });
  };

  const getLineItemSize = (lineItems: AddToBasketMutation['addToBasket']['lineItems']) => {
    const addedItem = lineItems.find(
      (item) =>
        getFragmentData(TrackGlycerinAddToCartLineItemFragmentDoc, item).product.sku === sku,
    );
    return addedItem
      ? (getFragmentData(TrackGlycerinAddToCartLineItemFragmentDoc, addedItem).product.size ??
          undefined)
      : undefined;
  };

  const trackAddToCart = ({
    lineItems,
  }: {
    lineItems: AddToBasketMutation['addToBasket']['lineItems'];
  }) => {
    const lineItemSize = getLineItemSize(lineItems);

    dispatchGTMEvent({
      Merkzettel: 'nein',
      ecommerce: {
        add: {
          products: [
            {
              brand: brand?.name,
              category: navq ? `search:${navq}` : breadcrumbs?.join('/'),
              description: longDescription || undefined,
              dimension12: price.value > 0 ? 'nein' : 'ja',
              dimension15: lineItemSize,
              dimension16: color,
              dimension18: price.saving?.value! > 0 ? 'ja' : 'nein',
              dimension19: promotion || undefined,
              dimension20: availability.message,
              dimension21: brand?.name,
              id: orderNumber,
              imageUrl: media?.[0].url || undefined,
              marketcode: marketcode || undefined,
              name,
              price: price.value / 100,
              quantity,
              sku,
              variant,
            },
          ],
        },
        cart: {
          products: lineItems,
        },
      },
      event: 'EECaddToCart',
    });
  };

  const trackCatalogAddedProduct = ({
    lineItems,
  }: {
    lineItems: AddToBasketMutation['addToBasket']['lineItems'];
  }) => {
    const lineItemSize = getLineItemSize(lineItems);

    dispatchGTMEvent({
      event: 'ga_event',
      eventAction: 'viewLayer',
      eventCategory: 'catalog_addedproduct',
      eventValue: {
        orderNumber: sku.concat(promotion || ''),
        price: price.value,
        size: lineItemSize,
      },
    });
  };

  const trackGlycerinAddToCart = ({
    lineItems,
  }: {
    lineItems: AddToBasketMutation['addToBasket']['lineItems'];
  }) => {
    dispatchGTMEvent<GTMEventGlycerinAddToCart>({
      event: 'AddToCart',
      AddToCartData: {
        addedProducts: [
          {
            ...getGlycerinArticleId({ orderNumber, promotion }),
            productId: akl,
            skuId: sku,
            quantity,
            currency: price.currency,
            displayedPrice: price.value,
            originalPrice: price.valueOld || undefined,
            ...getGlycerinAvailability(availability),
            services: addToBasketTrackingAplus
              ? getGlycerinServices({ aplus: addToBasketTrackingAplus, currency: price.currency })
              : getAplusDataForTracking(aPlusData, price.currency),
            ...getGlycerinOrigin(ref),
          },
        ],
        custom: {
          gtmData: {
            products: lineItems.map((item) => {
              const { extra, product } = getFragmentData(
                TrackGlycerinAddToCartLineItemFragmentDoc,
                item,
              );

              const {
                name: lineItemName,
                orderNumber: lineItemOrderNumber,
                size: lineItemSize,
                variation: lineItemVariation,
              } = product;

              return {
                orderNumber: lineItemOrderNumber,
                productName: lineItemName,
                promotion: extra?.promotion || '',
                size: lineItemSize,
                variant: lineItemVariation || '',
              };
            }),
          },
        },
        passive: false,
      },
    });
  };

  return {
    getTrackingParameter,
    trackLayerClosed,
    trackToBasketClick,
    trackToCheckAndOrder,
    trackAddToCart,
    trackCatalogAddedProduct,
    trackGlycerinAddToCart,
    trackUpdateCart,
  };
};
